/**
 * Products Redux Slice
 */

import { createSlice, type PayloadAction } from '@spcc/core'
import type {
  SKU,
  Variant,
  VariantPortalAvailability,
  Category,
  ProductsStats,
  ProductsFilter,
  CreateSKURequest,
  UpdateSKURequest,
  CreateVariantRequest,
  UpdateVariantRequest,
  CreateCategoryRequest,
  UpdateCategoryRequest,
} from '../domain/types'
import { DEFAULT_PRODUCTS_FILTER, DEFAULT_PRODUCTS_STATS } from '../domain/constants'
import { calculateProductsStats } from '../domain/utils'

// ============================================================================
// State Interface
// ============================================================================

export interface ProductsState {
  skus: SKU[]
  variants: Variant[]
  categories: Category[]
  selectedSkuId: string | null
  selectedVariantId: string | null
  // Full-hydrated items for edit forms (includes both EN + AR localized fields)
  editingSku: SKU | null
  editingVariant: Variant | null
  editingCategory: Category | null
  editingLoading: boolean
  stats: ProductsStats
  filter: ProductsFilter
  loading: boolean
  saving: boolean
  portalAvailabilityUpdatingById: Record<string, boolean>
  error: string | null
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: ProductsState = {
  skus: [],
  variants: [],
  categories: [],
  selectedSkuId: null,
  selectedVariantId: null,
  editingSku: null,
  editingVariant: null,
  editingCategory: null,
  editingLoading: false,
  stats: DEFAULT_PRODUCTS_STATS,
  filter: DEFAULT_PRODUCTS_FILTER,
  loading: false,
  saving: false,
  portalAvailabilityUpdatingById: {},
  error: null,
}

// ============================================================================
// Slice
// ============================================================================

export const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    // ==========================================
    // Fetch all products data
    // ==========================================
    fetchProducts: (state) => {
      state.loading = true
      state.error = null
    },

    fetchProductsSuccess: (
      state,
      action: PayloadAction<{
        skus: SKU[]
        variants: Variant[]
        categories: Category[]
      }>
    ) => {
      state.skus = action.payload.skus
      state.variants = action.payload.variants
      state.categories = action.payload.categories
      state.loading = false
      state.stats = calculateProductsStats(
        action.payload.skus,
        action.payload.variants,
        action.payload.categories
      )
    },

    fetchProductsFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // ==========================================
    // SKU CRUD
    // ==========================================
    createSku: (state, _action: PayloadAction<CreateSKURequest>) => {
      state.saving = true
      state.error = null
    },

    createSkuSuccess: (state, action: PayloadAction<SKU>) => {
      state.skus = [...state.skus, action.payload]
      state.saving = false
      state.stats = calculateProductsStats(state.skus, state.variants, state.categories)
    },

    createSkuFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.saving = false
    },

    updateSku: (state, _action: PayloadAction<UpdateSKURequest>) => {
      state.saving = true
      state.error = null
    },

    updateSkuSuccess: (state, action: PayloadAction<SKU>) => {
      state.skus = state.skus.map((sku) =>
        sku.documentId === action.payload.documentId ? action.payload : sku
      )
      state.saving = false
      state.stats = calculateProductsStats(state.skus, state.variants, state.categories)
    },

    updateSkuFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.saving = false
    },

    deleteSku: (state, _action: PayloadAction<string>) => {
      state.saving = true
      state.error = null
    },

    deleteSkuSuccess: (state, action: PayloadAction<string>) => {
      state.skus = state.skus.filter((sku) => sku.documentId !== action.payload)
      // Also remove variants belonging to this SKU
      state.variants = state.variants.filter((v) => v.skuDocumentId !== action.payload)
      state.saving = false
      state.stats = calculateProductsStats(state.skus, state.variants, state.categories)
    },

    deleteSkuFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.saving = false
    },

    selectSku: (state, action: PayloadAction<string | null>) => {
      state.selectedSkuId = action.payload
    },

    // ==========================================
    // Variant CRUD
    // ==========================================
    createVariant: (state, _action: PayloadAction<CreateVariantRequest>) => {
      state.saving = true
      state.error = null
    },

    createVariantSuccess: (state, action: PayloadAction<Variant>) => {
      state.variants = [...state.variants, action.payload]
      state.saving = false
      state.stats = calculateProductsStats(state.skus, state.variants, state.categories)
    },

    createVariantFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.saving = false
    },

    updateVariant: (state, _action: PayloadAction<UpdateVariantRequest>) => {
      state.saving = true
      state.error = null
    },

    updateVariantSuccess: (state, action: PayloadAction<Variant>) => {
      state.variants = state.variants.map((v) =>
        v.documentId === action.payload.documentId ? action.payload : v
      )
      state.saving = false
      state.stats = calculateProductsStats(state.skus, state.variants, state.categories)
    },

    updateVariantFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.saving = false
    },

    deleteVariant: (state, _action: PayloadAction<string>) => {
      state.saving = true
      state.error = null
    },

    deleteVariantSuccess: (state, action: PayloadAction<string>) => {
      state.variants = state.variants.filter((v) => v.documentId !== action.payload)
      state.saving = false
      state.stats = calculateProductsStats(state.skus, state.variants, state.categories)
    },

    deleteVariantFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.saving = false
    },

    updateVariantPortalAvailability: (
      state,
      _action: PayloadAction<{ documentId: string; portalAvailability: VariantPortalAvailability }>
    ) => {
      state.error = null
      state.portalAvailabilityUpdatingById[_action.payload.documentId] = true
    },

    updateVariantPortalAvailabilitySuccess: (
      state,
      action: PayloadAction<{ documentId: string; portalAvailability: VariantPortalAvailability }>
    ) => {
      state.variants = state.variants.map((variant) =>
        variant.documentId === action.payload.documentId
          ? { ...variant, portalAvailability: action.payload.portalAvailability }
          : variant
      )
      delete state.portalAvailabilityUpdatingById[action.payload.documentId]
      state.stats = calculateProductsStats(state.skus, state.variants, state.categories)
    },

    updateVariantPortalAvailabilityFailure: (
      state,
      action: PayloadAction<{ documentId: string; message: string }>
    ) => {
      state.error = action.payload.message
      delete state.portalAvailabilityUpdatingById[action.payload.documentId]
    },

    selectVariant: (state, action: PayloadAction<string | null>) => {
      state.selectedVariantId = action.payload
    },

    // ==========================================
    // Category CRUD
    // ==========================================
    createCategory: (state, _action: PayloadAction<CreateCategoryRequest>) => {
      state.saving = true
      state.error = null
    },

    createCategorySuccess: (state, action: PayloadAction<Category>) => {
      state.categories = [...state.categories, action.payload]
      state.saving = false
      state.stats = calculateProductsStats(state.skus, state.variants, state.categories)
    },

    createCategoryFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.saving = false
    },

    updateCategory: (state, _action: PayloadAction<UpdateCategoryRequest>) => {
      state.saving = true
      state.error = null
    },

    updateCategorySuccess: (state, action: PayloadAction<Category>) => {
      state.categories = state.categories.map((cat) =>
        cat.documentId === action.payload.documentId ? action.payload : cat
      )
      state.saving = false
      state.stats = calculateProductsStats(state.skus, state.variants, state.categories)
    },

    updateCategoryFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.saving = false
    },

    // ==========================================
    // Fetch for edit (bilingual – EN + AR)
    // ==========================================
    fetchSkuForEdit: (state, _action: PayloadAction<string>) => {
      state.editingLoading = true
      state.editingSku = null
    },
    fetchSkuForEditSuccess: (state, action: PayloadAction<SKU>) => {
      state.editingSku = action.payload
      state.editingLoading = false
      // Keep the list in sync too
      state.skus = state.skus.map((s) =>
        s.documentId === action.payload.documentId ? action.payload : s
      )
    },
    fetchSkuForEditFailure: (state, action: PayloadAction<string>) => {
      state.editingLoading = false
      state.error = action.payload
    },

    fetchVariantForEdit: (state, _action: PayloadAction<string>) => {
      state.editingLoading = true
      state.editingVariant = null
    },
    fetchVariantForEditSuccess: (state, action: PayloadAction<Variant>) => {
      state.editingVariant = action.payload
      state.editingLoading = false
      state.variants = state.variants.map((v) =>
        v.documentId === action.payload.documentId ? action.payload : v
      )
    },
    fetchVariantForEditFailure: (state, action: PayloadAction<string>) => {
      state.editingLoading = false
      state.error = action.payload
    },

    fetchCategoryForEdit: (state, _action: PayloadAction<string>) => {
      state.editingLoading = true
      state.editingCategory = null
    },
    fetchCategoryForEditSuccess: (state, action: PayloadAction<Category>) => {
      state.editingCategory = action.payload
      state.editingLoading = false
      state.categories = state.categories.map((c) =>
        c.documentId === action.payload.documentId ? action.payload : c
      )
    },
    fetchCategoryForEditFailure: (state, action: PayloadAction<string>) => {
      state.editingLoading = false
      state.error = action.payload
    },

    clearEditingItems: (state) => {
      state.editingSku = null
      state.editingVariant = null
      state.editingCategory = null
      state.editingLoading = false
    },

    // ==========================================
    // Filter
    // ==========================================
    setFilter: (state, action: PayloadAction<Partial<ProductsFilter>>) => {
      state.filter = { ...state.filter, ...action.payload }
    },

    resetFilter: (state) => {
      state.filter = DEFAULT_PRODUCTS_FILTER
    },

    // ==========================================
    // Error handling
    // ==========================================
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
      state.saving = false
    },

    clearError: (state) => {
      state.error = null
    },

    // ==========================================
    // Reset
    // ==========================================
    reset: () => initialState,
  },
})

export const productsActions = productsSlice.actions
export const productsReducer = productsSlice.reducer
